import axios from 'axios';
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  ref,
  watch,
  watchEffect,
  WatchStopHandle,
} from 'vue';
import { ClubService } from '@/api/services/club.service';
import { WhatsNewService } from '@/api/services/whats-new.service';
import {
  WHATS_NEW_MODAL_NAME,
  WHATS_NEW_QUERY,
} from '@/domain/whats-new/whats-new.const';
import { IClubWhatsNewInput } from '@/domain/whats-new/club-whats-new-eligibility';
import {
  IWhatsNewEntry,
  WHATS_NEW_REGISTRY,
} from '@/domain/whats-new/whats-new.registry';
import { PermissionType } from '@/enums/PermissionType';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { vueData } from '@/VueData';
import { useAuth, useAuthStore } from './use-auth';
import { useModal } from './use-modal';
import type { IDynamicModalEntry, TRootWithDynamicModals } from '@/lib/modals';

const GET_PROGRAM_TIMEOUT_MS = 2000;
const OPEN_DEBOUNCE_MS = 450;

/**
 * "What's New" announcement host. Call exactly once, from
 * AppSetupContextWorkaround. Walks WHATS_NEW_REGISTRY and opens the first
 * eligible entry after the gates settle; persists seen state server-side
 * (user-variables) through PUT /auth/whats-new.
 */
export function useWhatsNew() {
  const proxy = getCurrentInstance()!.proxy;
  const authStore = useAuthStore();
  const authHelper = useAuth();
  const restaurantStore = useRestaurantStore();
  const modals = useModal();

  //#region Reactive inputs

  const user = computed(() => authStore.user);
  const restaurant = computed(() => restaurantStore.restaurant);
  const routePath = computed(() => proxy.$route?.path ?? '');
  const isPos = computed(() => !!proxy.$route?.meta?.pos);
  const forcedId = computed(() => {
    const raw = proxy.$route?.query?.[WHATS_NEW_QUERY];
    return typeof raw === 'string' ? raw : null;
  });
  const hasClubManage = computed(() =>
    authHelper.hasPermission(PermissionType.ClubManage)
  );
  const effectiveTier = computed(() => restaurantStore.effectiveTier);
  const seenWhatsNew = computed(() => user.value?.seenWhatsNew ?? []);
  // The Club route has no name; exact-path match on the current restaurant.
  const isClubRoute = computed(
    () =>
      restaurant.value != null &&
      routePath.value === `/${restaurant.value.stringId}/club`
  );

  //#endregion

  //#region Session + program state

  // User-session flags (implicitly keyed by user.id: reset on id change).
  let modalOpen = false;
  let dismissedThisSession = false;
  let suppressPersistOnce = false;
  let openedAsForcePreview = false;
  /** Single-flight `${userId}:${id}` PUTs (also blocks same-session retries). */
  const markRequested = new Set<string>();

  const programKnown = ref(false);
  const programExists = ref(false);
  let programGeneration = 0;
  /** Fetch-once key: retry only when user/restaurant/permissions change. */
  let programFetchedKey: string | null = null;

  let openTimer: number | null = null;
  let occupancyStop: WatchStopHandle | null = null;

  function clearOpenTimer() {
    if (openTimer != null) {
      window.clearTimeout(openTimer);
      openTimer = null;
    }
  }

  function stopOccupancyWait() {
    if (occupancyStop) {
      occupancyStop();
      occupancyStop = null;
    }
  }

  function resetProgramState() {
    programGeneration++;
    programFetchedKey = null;
    programKnown.value = false;
    programExists.value = false;
  }

  //#endregion

  //#region getProgram

  const shouldFetchProgram = computed(() => {
    if (!user.value || !restaurant.value) return false;
    if (effectiveTier.value < 1) return false;
    if (!hasClubManage.value) return false;
    if (isPos.value) return false;
    if (isClubRoute.value) return false;
    const entry = WHATS_NEW_REGISTRY[0];
    const seen = seenWhatsNew.value.includes(entry.id);
    return forcedId.value === entry.id || !seen;
  });

  function maybeFetchProgram() {
    if (!shouldFetchProgram.value) return;
    if (dismissedThisSession) return;
    const key = `${user.value!.id}:${restaurant.value!.id}:${
      hasClubManage.value
    }:${effectiveTier.value}`;
    if (programFetchedKey === key) return;
    programFetchedKey = key;
    void fetchProgram(++programGeneration, restaurant.value!.id);
  }

  async function fetchProgram(generation: number, restaurantId: number) {
    let timeoutHandle: number | null = null;
    try {
      const timeout = new Promise<never>((_, reject) => {
        timeoutHandle = window.setTimeout(
          () => reject(new Error('whats-new getProgram timeout')),
          GET_PROGRAM_TIMEOUT_MS
        );
      });
      const program = await Promise.race([
        ClubService.getProgram(restaurantId),
        timeout,
      ]);
      if (generation !== programGeneration) return; // stale settle
      programKnown.value = true;
      // Active OR paused both count as "exists" (never re-sell over a
      // paused program). restaurant.club only carries active programs.
      programExists.value = program != null;
    } catch (error) {
      if (generation !== programGeneration) return;
      programKnown.value = false;
      programExists.value = false;
      console.warn('[whats-new] getProgram failed', error);
    } finally {
      if (timeoutHandle != null) window.clearTimeout(timeoutHandle);
    }
  }

  watch(
    [
      () => user.value?.id ?? null,
      () => restaurant.value?.id ?? null,
      hasClubManage,
      effectiveTier,
      shouldFetchProgram,
    ],
    () => maybeFetchProgram(),
    { immediate: true }
  );

  //#endregion

  //#region Persistence

  function persistSeen(id: string) {
    const current = vueData.user;
    if (!current) return;
    const seen = current.seenWhatsNew ?? [];
    if (!seen.includes(id)) {
      // setUser updates Pinia too; the shell (App.vue props) reads vueData,
      // so never write Pinia-only.
      vueData.setUser({ ...current, seenWhatsNew: [...seen, id] });
    }

    const key = `${current.id}:${id}`;
    if (markRequested.has(key)) return;
    markRequested.add(key);

    WhatsNewService.markSeen(id).catch((error) => {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      // API not deployed yet: closed locally this session; a later full
      // reload may show the dialog again until the endpoint exists.
      if (status !== 404 && status !== 501) {
        console.error('[whats-new] markSeen failed', error);
        vueData.logException(error, 'WhatsNew markSeen', true);
      }
    });
  }

  //#endregion

  //#region Evaluation + opening

  function buildInput(entry: IWhatsNewEntry): IClubWhatsNewInput {
    return {
      seenWhatsNew: seenWhatsNew.value,
      forcePreview: forcedId.value === entry.id,
      isLoggedIn: !!user.value,
      hasRestaurant: !!restaurant.value,
      effectiveTier: effectiveTier.value,
      hasClubManage: hasClubManage.value,
      isPos: isPos.value,
      isClubRoute: isClubRoute.value,
      programKnown: programKnown.value,
      programExists: programExists.value,
    };
  }

  function evaluateAll() {
    if (modalOpen) return;
    for (const entry of WHATS_NEW_REGISTRY) {
      const evaluation = entry.evaluate(buildInput(entry));
      if (evaluation.markSeen) {
        // They reached the feature by themselves (Club route + ClubManage).
        persistSeen(entry.id);
        continue;
      }
      if (evaluation.show) {
        scheduleOpen(entry);
        return;
      }
    }
    clearOpenTimer();
  }

  function scheduleOpen(entry: IWhatsNewEntry) {
    if (modalOpen || dismissedThisSession) return;
    clearOpenTimer();
    openTimer = window.setTimeout(() => {
      openTimer = null;
      tryOpen(entry);
    }, OPEN_DEBOUNCE_MS);
  }

  function tryOpen(entry: IWhatsNewEntry) {
    if (modalOpen || dismissedThisSession) return;
    // Gates may have flipped during the debounce — re-check right now.
    if (!entry.evaluate(buildInput(entry)).show) return;
    if (otherModalOpen()) {
      waitForModalsToClose(entry);
      return;
    }
    openModal(entry);
  }

  function dynamicModals(): IDynamicModalEntry[] {
    const root = proxy.$root as TRootWithDynamicModals;
    return root._dynamicContainer?.modals ?? [];
  }

  function otherModalOpen(): boolean {
    return dynamicModals().some(
      (modal) => modal.modalAttrs?.name !== WHATS_NEW_MODAL_NAME
    );
  }

  /** Wait (don't skip the session) until every other dynamic modal closes. */
  function waitForModalsToClose(entry: IWhatsNewEntry) {
    stopOccupancyWait();
    // The container exists here (a modal is open), so the getter tracks its
    // reactive modals array.
    occupancyStop = watch(otherModalOpen, (occupied) => {
      if (occupied) return;
      stopOccupancyWait();
      tryOpen(entry);
    });
  }

  function openModal(entry: IWhatsNewEntry) {
    openedAsForcePreview = forcedId.value === entry.id;
    modalOpen = true;
    modals.show(
      entry.modal,
      { forcePreview: openedAsForcePreview },
      {
        name: WHATS_NEW_MODAL_NAME,
        adaptive: true,
        height: 'auto',
        scrollable: true,
        clickToClose: false,
        focusTrap: true,
        width: 640,
        maxWidth: Math.round(window.innerWidth * 0.95),
      },
      { closed: () => onClosed(entry) }
    );
  }

  /** Single persist path: covers X, "Ahora no", Escape and "Ir a Club". */
  function onClosed(entry: IWhatsNewEntry) {
    modalOpen = false;
    const suppressed = suppressPersistOnce;
    suppressPersistOnce = false;
    if (suppressed) {
      // Restaurant/user switch hid us without persist. Re-run now that
      // modalOpen is false so the new context can show or markSeen.
      evaluateAll();
      return;
    }
    dismissedThisSession = true;
    if (!openedAsForcePreview) persistSeen(entry.id);
  }

  watch(
    [
      () => user.value?.id ?? null,
      () => restaurant.value?.id ?? null,
      seenWhatsNew,
      hasClubManage,
      effectiveTier,
      routePath,
      isPos,
      forcedId,
      programKnown,
      programExists,
    ],
    () => evaluateAll(),
    { immediate: true }
  );

  //#endregion

  //#region Resets (user / restaurant / route)

  watch(
    () => user.value?.id ?? null,
    (newId, oldId) => {
      if (newId === oldId) return;
      // First hydrate is null → id. The fetch watcher already started; a
      // reset here would bump generation and discard that in-flight result.
      if (oldId == null) return;
      // Logout → login without reload: AppSetupContextWorkaround stays
      // mounted, so wipe every per-user flag.
      dismissedThisSession = false;
      openedAsForcePreview = false;
      markRequested.clear();
      resetProgramState();
      clearOpenTimer();
      stopOccupancyWait();
      if (modalOpen) {
        suppressPersistOnce = true;
        modals.hide(WHATS_NEW_MODAL_NAME);
      }
      maybeFetchProgram();
    }
  );

  watch(
    () => restaurant.value?.id ?? null,
    (newId, oldId) => {
      if (newId === oldId) return;
      // Same first-hydrate trap as the user watcher.
      if (oldId == null) return;
      resetProgramState();
      clearOpenTimer();
      stopOccupancyWait();
      if (modalOpen) {
        // Hide without persist. onClosed re-evaluates; a Club-route
        // landing with ClubManage persists via the markSeen rule.
        suppressPersistOnce = true;
        modals.hide(WHATS_NEW_MODAL_NAME);
      }
      maybeFetchProgram();
    }
  );

  //#endregion

  //#region Sidebar chip

  watchEffect(() => {
    const entry = WHATS_NEW_REGISTRY[0];
    const seen = seenWhatsNew.value.includes(entry.id);
    const force = forcedId.value === entry.id;
    // Chip only when the dialog itself is (or was) warranted: never on
    // stores that already have Club, never for ClubTopup-only users.
    vueData.clubLaunchChip =
      !!user.value &&
      !!restaurant.value &&
      effectiveTier.value >= 1 &&
      hasClubManage.value &&
      programKnown.value &&
      !programExists.value &&
      (force || !seen);
  });

  //#endregion

  onBeforeUnmount(() => {
    clearOpenTimer();
    stopOccupancyWait();
  });
}
